import {
  Component,
  Input,
  OnChanges
} from '@angular/core';

import { HaynesProDiagnosticToolSection } from '../typings/haynes-pro.diagnostic-tool.section';
import { ImageViewerService }             from '../../shared/components/image-viewer/image-viewer.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-diagnostic-details',
  template: require('./haynes-pro.diagnostic-tool.diagnostic-details.component.html'),
  styles: [require('./haynes-pro.diagnostic-tool.diagnostic-details.component.scss')]
})
export class HaynesProDiagnosticToolDiagnosticDetailsComponent implements OnChanges {
  @Input() diagnosticDetails: any;
  @Input() dataType!        : string;
  @Input() maxHeight!       : string;

  sections : HaynesProDiagnosticToolSection[] = [];

  selectedIndex = 0;

  constructor(
    private imageViewerService: ImageViewerService
  ) {}

  ngOnChanges() {
    const data = this.getRawArray();

    switch (this.dataType) {
      case 'LUBRICANTS':
        this.sections = this.transformLubricants(data);
        break;

      case 'ADJUSTMENTS':
        this.sections = this.transformAdjustments(data);
        break;

      case 'WARNING_LIGHTS':
        this.sections      = this.transformWarningLights(data);
        this.selectedIndex = 0;
        break;

      case 'DRAWINGS':
        this.sections = this.transformDrawings(data);
        break;

      case 'LOCATIONS':
        this.sections = this.transformLocations(data);
        break;

      default:
        this.sections = [];
    }
  }

  getRawArray(): any[] {
    const d = this.diagnosticDetails;
    if (this.dataType === 'LUBRICANTS') {
      return Array.isArray(d) ? d : (d?.lubricants || []);
    }
    if (this.dataType === 'ADJUSTMENTS') {
      return Array.isArray(d) ? d : (d?.items || []);
    }
    if (this.dataType === 'WARNING_LIGHTS') {
      return Array.isArray(d) ? d : (d?.warning_lights || []);
    }
    if (this.dataType === 'DRAWINGS') {
      return Array.isArray(d) ? d : (d?.drawings || []);
    }
    if (this.dataType === 'LOCATIONS') {
      return Array.isArray(d) ? d : (d?.locations || []);
    }
    return [];
  }

  get selectedSection(): HaynesProDiagnosticToolSection | null {
    return this.sections[this.selectedIndex] || null;
  }

  objectKeys( row : any ) : string[] {
    return Object.keys(row || {}).filter( key => !key.startsWith('_') );
  }

  selectWarning(i: number) {
    this.selectedIndex = i;
  }

  transformLubricants( data : any[] ) : HaynesProDiagnosticToolSection[] {
    return (data || []).map(group => {
      const rows: any[] = [];

      (group.lubricantItems || []).forEach(( item : any ) => {
        const qualityText =
          item.quality && item.temperature
            ? `${item.quality} - ${item.temperature}`
            : (item.quality || item.temperature || '');

        rows.push({
          Description: item.name,
          Viscosity  : item.viscosity,
          Quality    : qualityText
        });
      });

      let lastRow: any | null = null;

      (group.smartLinks || []).forEach(( link : any ) => {
        if (link.operation === 'IMAGE' && link.id2) {
          if (lastRow) {
            if (!lastRow._images) {
              lastRow._images = [];
            }
            lastRow._images.push( link.id2 );
          }
          return;
        }

        const text =
          Array.isArray( link.text )
            ? link.text.join(' ')
            : '';

        if (!text || !text.trim()) {
          return;
        }

        const row: any = {
          Description: text,
          Viscosity  : '',
          Quality    : ''
        };

        rows.push(row);
        lastRow = row;
      });

      return {
        header     : group.name,
        subSections: [
          {
            header: '',
            rows,
            notes : [],
            images: []
          }
        ]
      };
    });
  }

  transformAdjustments( data : any[] ) : HaynesProDiagnosticToolSection[] {
    const sortByOrder = ( arr : any[] = [] ) => [...arr].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    return (data || []).map(sec => ({
      header     : sec.section,
      subSections: sortByOrder(sec.items || []).map(( block : any ) => {
        const rows: any[] = [];

        sortByOrder(block.items || []).forEach(( item : any ) => {
          const children    = item.items || [];
          const hasChildren = Array.isArray(children) && children.length > 0;

          const parentRow: any = {
            Name : item.name,
            Value: item.value,
            Unit : item.unit
          };

          if (item.remark) {
            parentRow._remark = item.remark;
          }

          if (item.image_url) {
            parentRow._images = [ item.image_url ];
          }

          rows.push(parentRow);

          // Nested rows (children)
          if (hasChildren) {
            sortByOrder(children).forEach(( child : any ) => {
              const childRow: any = {
                Name    : child.name,
                Value   : child.value,
                Unit    : child.unit,
                _isChild: true
              };

              if (child.remark) {
                childRow._remark = child.remark;
              }

              if (child.image_url) {
                childRow._images = [ child.image_url ];
              }

              rows.push(childRow);
            });
          }
        });

        return {
          header: block.name,
          rows,
          notes : [],
          images: []
        };
      })
    }));
  }

  transformWarningLights(data: any[]): HaynesProDiagnosticToolSection[] {
    return data.map(w => ({
      header     : '',
      subSections: [
        {
          header: 'Description',
          rows  : (w.story_lines.find((s: any) => s.label === 'Description')?.items || [])
                    .map((txt: string) => ({ Description: txt })),
          images: [ w.image ]
        },
        {
          header: 'Solution',
          rows  : (w.story_lines.find((s: any) => s.label === 'Solution')?.items || [])
                    .map((txt: string) => ({ Solution: txt }))
        }
      ]
    }));
  }

  transformDrawings(data: any[]): HaynesProDiagnosticToolSection[] {
    return (data || []).map(group => ({
      header     : group.group_name,
      subSections: (group.sub_drawings || []).map((sd: any) => ({
        header: sd?.description || '',
        images: sd?.image_url ? [sd.image_url] : [],
        rows  : [],
        notes : []
      }))
    }));
  }

  transformLocations(data: any[]): HaynesProDiagnosticToolSection[] {
    return (data || []).map(group => {
      const subSections = (group.sub_items || []).map((mid: any) => {
        const leaves = mid?.sub_items || [];

        const rows = leaves
          .filter((leaf: any) => (leaf?.name?.trim?.() || '').length > 0)
          .map((leaf: any) => ({
            Text: leaf.remark ? `${leaf.name} ${leaf.remark}` : leaf.name
          }));

        const images = leaves
          .filter((leaf: any) => !!leaf.image)
          .map((leaf: any) => leaf.image);

        return {
          header: mid?.name || '',
          rows,
          images,
          notes : []
        };
      });

      return {
        header: group.category || '',
        subSections
      };
    });
  }

  viewImage (src : string ) : void {
    this.imageViewerService.view(src);
  }

}